import { pageTitle } from '@palaccount/config';
import {
  ASSET_CATEGORY_KEYS,
  ASSET_STATUSES,
  DEPRECIATION_METHODS,
  FA_MESSAGES,
  type AssetCategoryKey,
  type AssetResponseFull,
  type AssetStatus,
  type DepreciationMethod,
} from '@palaccount/contracts';
import { Play, Plus, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { TableCell } from '@/components/ui/table';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { Modal } from '@/features/inventory/modal';
import { useCan } from '@/lib/permissions';
import { readableTextOn } from '@/lib/readable-color';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useCompanyToday, useDocumentDate } from '@/lib/session';

import {
  useAddMaintenance,
  useAsset,
  useAssetCategories,
  useAssets,
  useCreateAsset,
  useDepreciationPreview,
  useDepreciationRuns,
  useDisposalPreview,
  useDisposeAsset,
  usePostDepreciation,
  useRemoveMaintenance,
  useUpdateAsset,
  useUpdateCategory,
} from './api';

/**
 * FIN-07 — Fixed Assets. §4's layout: a KPI strip, a toolbar, then a
 * standing asset list beside the four detail cards (valuation, identity,
 * depreciation schedule, maintenance log) — all on the ONE screen for every
 * user who can reach it (§9, ruling R1).
 *
 * The prototype defects the screen must NOT reproduce:
 *   - the status dropdown offered Disposed; here disposal is only ever the
 *     dialogue, which posts the entry (§6, Q-FIN-07-4);
 *   - a disposed asset looked read-only but typed; here the fields are
 *     genuinely disabled and the server and DB refuse anyway;
 *   - the percentage said "of cost" while dividing by the depreciable base;
 *     the label says what the sum does (§5d).
 *
 * The chip is DERIVED (BR-FIN-07-15) and the status filter matches the CHIP
 * (BR-FIN-07-16) — both decided by the server, drawn here.
 */

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold';

const STATUS_META: Record<AssetStatus, { key: string; chip: string }> = {
  /**
   * D32's draft state. The label is the product-wide draft word
   * (`common.status.draft`) and the fill is the exact pair every document
   * draft wears in `status-chip.tsx` — NFR-14's "grey = not started, or a
   * draft", copied verbatim rather than chosen.
   */
  draft: { key: 'common.status.draft', chip: 'bg-muted text-muted-foreground' },
  in_use: { key: 'fa.stInUse', chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  under_maintenance: { key: 'fa.stMaintenance', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  fully_depreciated: { key: 'fa.stFullyDepreciated', chip: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  disposed: { key: 'fa.stDisposed', chip: 'bg-muted text-muted-foreground' },
};

const METHOD_KEYS: Record<DepreciationMethod, string> = {
  straight_line: 'fa.mStraight',
  declining_balance: 'fa.mDeclining',
  units_of_production: 'fa.mUnits',
};

const CATEGORY_KEYS: Record<AssetCategoryKey, string> = {
  equipment: 'fa.catEquipment',
  vehicles: 'fa.catVehicles',
  buildings: 'fa.catBuildings',
  furniture: 'fa.catFurniture',
  it_hardware: 'fa.catItHardware',
  other: 'fa.catOther',
};

function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/** §4: a category-tinted square carrying the asset's initials. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word[0]!.toUpperCase())
    .join('');
}

export function AssetsPage() {
  const { t } = useTranslation();
  const canEdit = useCan('accounting.edit');
  // Kept, rather than moved to <Gated>, because BR-FIN-07-18 prescribes this
  // control's own refusal sentence — see the Category defaults button below.
  const canConfigure = useCan('admin.edit');
  // G-09: a newly registered asset is dated today rather than left undated.
  const today = useCompanyToday();

  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [runOpen, setRunOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('fa.title'));
  }, [t]);

  const { data } = useAssets({
    ...(category !== '' ? { category } : {}),
    ...(status !== '' ? { status: status as AssetStatus } : {}),
    ...(search.trim() !== '' ? { search: search.trim() } : {}),
  });
  const assets = useMemo(() => data?.assets ?? [], [data]);
  const kpis = data?.kpis;
  const { data: detail } = useAsset(selectedId);
  const create = useCreateAsset();

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('fa.eyebrow')}
        </p>
        <h1 className="text-2xl font-semibold">{t('fa.title')}</h1>
      </div>
      <AccountingTabs />

      {error !== '' ? (
        <p
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </p>
      ) : null}

      {/* §4's KPI strip. */}
      {kpis ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kpi label={t('fa.kpiCost')} value={<Amount value={kpis.totalCost} />} />
          <Kpi label={t('fa.kpiAccumulated')} value={<Amount value={kpis.accumulated} />} />
          <Kpi label={t('fa.kpiNbv')} value={<Amount value={kpis.netBookValue} />} />
          <Kpi label={t('fa.kpiActive')} value={String(kpis.activeCount)} />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Gated permission="accounting.approve" reasonKey="perm.cannot.runDepreciation">
          <Button onClick={() => setRunOpen(true)}>
            <Play className="me-1 h-4 w-4" />
            {t('fa.runDepreciation')}
          </Button>
        </Gated>
        <Gated permission="accounting.create" reasonKey="perm.cannot.registerAssets">
          <Button
            variant="outline"
            onClick={() => {
              setError('');
              // §6: a blank asset, no confirmation message, and it is selected.
              // G-09: blank except its purchase date, which is the company's today.
              create
                .mutateAsync({ purchaseDate: today })
                .then((asset) => setSelectedId(asset.id))
                .catch((cause: unknown) => {
                  setError(localizeServerCause(t, cause, 'fa.errSaveFailed'));
                });
            }}
          >
            <Plus className="me-1 h-4 w-4" />
            {t('fa.registerAsset')}
          </Button>
        </Gated>
        {/*
          NOT converted, and the exception is deliberate. BR-FIN-07-18 already
          asks for this control's exact shape — "Hiding the Category defaults
          panel must not be treated as that check" — AND it prescribes the
          sentence, word for word: "You do not have permission to change
          depreciation settings. Ask an administrator to grant it." The sweep's
          `perm.roleCannot` convention would replace a specified message with a
          generated one, which is a worse trade than the consistency is worth.
          The sr-only description is added so the sentence reaches a screen
          reader as well as a pointer, which is the one thing it lacked.
        */}
        <span
          className="inline-flex"
          {...(canConfigure ? {} : { 'data-permission-denied': 'admin.edit' })}
        >
          <Button
            variant="outline"
            disabled={!canConfigure}
            title={canConfigure ? undefined : localizeServerMessage(t, FA_MESSAGES.categoryConfigDenied)}
            aria-describedby={canConfigure ? undefined : 'fa-cat-reason'}
            onClick={() => setCategoriesOpen(true)}
          >
            <Settings2 className="me-1 h-4 w-4" />
            {t('fa.categoryDefaults')}
          </Button>
          {/* The SAME sentence as the tooltip above, deliberately — the pointer
              and the screen reader must hear one wording. Both go through the
              map, so they cannot drift apart in Arabic either. */}
          {canConfigure ? null : (
            <span id="fa-cat-reason" className="sr-only">
              {localizeServerMessage(t, FA_MESSAGES.categoryConfigDenied)}
            </span>
          )}
        </span>
        <div className="ms-auto flex flex-wrap gap-2">
          <NativeSelect
            aria-label={t('fa.filterCategory')}
            className="h-9 w-44"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="">{t('fa.allCategories')}</option>
            {ASSET_CATEGORY_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(CATEGORY_KEYS[key])}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label={t('fa.filterStatus')}
            className="h-9 w-44"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{t('fa.allStatuses')}</option>
            {ASSET_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(STATUS_META[value].key)}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[312px_1fr]">
        <Card>
          <CardContent className="space-y-2 p-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                {t('fa.listTitle')}{' '}
                <span className="text-muted-foreground">({assets.length})</span>
              </h2>
            </div>
            <Input
              aria-label={t('fa.searchLabel')}
              placeholder={t('fa.searchPlaceholder')}
              className="h-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {assets.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">{t('fa.emptyList')}</p>
            ) : (
              <ul className="space-y-1">
                {assets.map((asset) => (
                  <li key={asset.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(asset.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md p-2 text-start hover:bg-muted/60',
                        selectedId === asset.id && 'bg-muted',
                      )}
                    >
                      {/* NFR-11 — the ink is COMPUTED from the category
                          colour, never fixed white: the colour is data, and
                          fixed white on it is the rule's own named defect
                          (see lib/readable-color.ts). A draft with no
                          category yet (D32) wears the neutral fill instead
                          of an invented tint. */}
                      <span
                        aria-hidden
                        className={cn(
                          'flex size-8 shrink-0 items-center justify-center rounded-md text-xs font-bold',
                          asset.categoryColor === null && 'bg-muted text-muted-foreground',
                        )}
                        style={
                          asset.categoryColor !== null
                            ? {
                                backgroundColor: asset.categoryColor,
                                color: readableTextOn(asset.categoryColor),
                              }
                            : undefined
                        }
                      >
                        {initials(asset.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{asset.name}</span>
                        <span className="flex items-center gap-1">
                          <span className="rounded bg-muted px-1 font-mono text-[11px]">
                            <Ltr value={asset.code} />
                          </span>
                          <span className={cn(CHIP, STATUS_META[asset.chip].chip)}>
                            {t(STATUS_META[asset.chip].key)}
                          </span>
                        </span>
                      </span>
                      {/* D32: a draft's value is not yet knowable — an empty
                          reading, never a zero nobody typed. */}
                      {asset.netBookValue !== null ? (
                        <Amount className="text-sm" value={asset.netBookValue} />
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {detail ? (
          <AssetDetail
            key={detail.id}
            asset={detail}
            canEdit={canEdit}
            onError={setError}
          />
        ) : (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t('fa.pickAsset')}
            </CardContent>
          </Card>
        )}
      </div>

      {runOpen ? <RunDepreciationModal onClose={() => setRunOpen(false)} onError={setError} /> : null}
      {categoriesOpen ? (
        <CategoryDefaultsModal onClose={() => setCategoriesOpen(false)} onError={setError} />
      ) : null}
      <RunHistoryCard />
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function AssetDetail({
  asset,
  canEdit,
  onError,
}: {
  asset: AssetResponseFull;
  canEdit: boolean;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateAsset();
  const addMaintenance = useAddMaintenance();
  const removeMaintenance = useRemoveMaintenance();
  const [disposeOpen, setDisposeOpen] = useState(false);
  const [name, setName] = useState(asset.name);
  // G-09: a new service entry's date starts as the company's today.
  const today = useCompanyToday();
  const [serviceDate, setServiceDate] = useDocumentDate();
  const [serviceCost, setServiceCost] = useState('');
  const [serviceNote, setServiceNote] = useState('');

  // BR-FIN-07-13: disposed is genuinely read-only, not a banner over live
  // inputs. The server and the DB refuse too.
  const frozen = asset.status === 'disposed' || !canEdit;

  const act = (promise: Promise<unknown>) => {
    onError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own rather
      // than the browser's `Failed to fetch`. Every failure path on this screen
      // names `fa.errSaveFailed`: one screen, one sentence, its own key.
      onError(localizeServerCause(t, cause, 'fa.errSaveFailed'));
    });
  };

  const patch = (body: Parameters<typeof update.mutateAsync>[0]['body']) =>
    act(update.mutateAsync({ assetId: asset.id, body }));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold">{asset.name}</h2>
              <p className="text-xs text-muted-foreground">
                <Ltr value={asset.code} />
                {asset.categoryName !== null ? <> · {asset.categoryName}</> : null}
                {asset.method !== null ? <> · {t(METHOD_KEYS[asset.method])}</> : null}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn(CHIP, STATUS_META[asset.chip].chip)}>
                {t(STATUS_META[asset.chip].key)}
              </span>
              {/* §6: hidden once the asset is disposed. A fully depreciated
                  asset can still be disposed, which is correct. A DRAFT has
                  nothing on the books to dispose (D32) — the server refuses,
                  so the button is not offered. */}
              {asset.status !== 'disposed' && asset.status !== 'draft' ? (
                <Gated permission="accounting.approve" reasonKey="perm.cannot.disposeAssets">
                  <Button variant="destructive" size="sm" onClick={() => setDisposeOpen(true)}>
                    {t('fa.disposeAsset')}
                  </Button>
                </Gated>
              ) : null}
            </div>
          </div>

          {asset.status === 'disposed' ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              {localizeServerMessage(t, FA_MESSAGES.disposedReadOnly)}
            </p>
          ) : null}

          {/* D32: a draft's absent figures are drawn as absent — an em dash,
              never a zero the user did not type. */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile
              label={t('fa.tileCost')}
              value={asset.cost !== null ? <Amount value={asset.cost} /> : '—'}
            />
            <Tile label={t('fa.tileAccumulated')} value={<Amount value={asset.accumulated} />} />
            <Tile
              label={t('fa.tileNbv')}
              value={asset.netBookValue !== null ? <Amount value={asset.netBookValue} /> : '—'}
            />
            <Tile
              label={t('fa.tileSalvage')}
              value={asset.salvage !== null ? <Amount value={asset.salvage} /> : '—'}
            />
          </div>
          {/* §5d: the label says what the sum DOES — the defect's relabel. */}
          <p className="text-xs text-muted-foreground">
            {t('fa.pctDepreciated', { pct: asset.pctDepreciated })}
          </p>

          {asset.disposal !== null ? (
            <div className="rounded-md border p-3 text-sm">
              <p className="font-medium">
                {t('fa.disposedOn', { date: asset.disposal.disposalDate })} ·{' '}
                {t(`fa.method${asset.disposal.method}`)}
              </p>
              <p
                className={cn(
                  'font-semibold',
                  Number(asset.disposal.gainLoss) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
                )}
              >
                {Number(asset.disposal.gainLoss) >= 0 ? t('fa.gain') : t('fa.loss')}{' '}
                <Amount value={asset.disposal.gainLoss} />
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Identity card (§5a). */}
      <Card>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="fa-name">{t('fa.fName')}</Label>
            <Input
              id="fa-name"
              value={name}
              disabled={frozen}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => {
                if (name.trim() !== '' && name !== asset.name) patch({ name: name.trim() });
              }}
            />
          </div>
          <div>
            <Label htmlFor="fa-code">{t('fa.fCode')}</Label>
            <Input id="fa-code" readOnly className="bg-muted font-mono" value={asset.code} />
          </div>
          <div>
            <Label htmlFor="fa-category">{t('fa.fCategory')}</Label>
            <NativeSelect
              id="fa-category"
              value={asset.categoryKey ?? ''}
              disabled={frozen}
              onChange={(event) => {
                if (event.target.value !== '') {
                  patch({ categoryKey: event.target.value as AssetCategoryKey });
                }
              }}
            >
              {/* D32: a draft may not have chosen yet — shown as the empty
                  choice, never a silent Equipment. Gone once one is picked
                  (a live asset always has a category). */}
              {asset.categoryKey === null ? <option value="">{t('common.choose')}</option> : null}
              {ASSET_CATEGORY_KEYS.map((key) => (
                <option key={key} value={key}>
                  {t(CATEGORY_KEYS[key])}
                </option>
              ))}
            </NativeSelect>
            {/* BR-FIN-07-06, stated where it bites. */}
            <p className="mt-1 text-xs text-muted-foreground">{t('fa.categoryReapplies')}</p>
          </div>
          <div>
            <Label htmlFor="fa-purchase">{t('fa.fPurchaseDate')}</Label>
            <Input
              id="fa-purchase"
              type="date"
              value={asset.purchaseDate ?? ''}
              disabled={frozen}
              onChange={(event) => {
                if (event.target.value !== '') patch({ purchaseDate: event.target.value });
              }}
            />
          </div>
          <div>
            <Label htmlFor="fa-cost">{t('fa.fCost')}</Label>
            <Input
              id="fa-cost"
              inputMode="decimal"
              defaultValue={asset.cost ?? ''}
              disabled={frozen}
              onBlur={(event) => {
                if (event.target.value !== (asset.cost ?? '') && event.target.value !== '') {
                  patch({ cost: event.target.value });
                }
              }}
            />
          </div>
          <div>
            <Label htmlFor="fa-salvage">{t('fa.fSalvage')}</Label>
            <Input
              id="fa-salvage"
              inputMode="decimal"
              defaultValue={asset.salvage ?? ''}
              disabled={frozen}
              onBlur={(event) => {
                if (event.target.value !== (asset.salvage ?? '') && event.target.value !== '') {
                  patch({ salvage: event.target.value });
                }
              }}
            />
          </div>
          <div>
            <Label htmlFor="fa-supplier">{t('fa.fSupplier')}</Label>
            <Input
              id="fa-supplier"
              defaultValue={asset.supplier ?? ''}
              disabled={frozen}
              onBlur={(event) => {
                if (event.target.value !== (asset.supplier ?? '')) {
                  patch({ supplier: event.target.value });
                }
              }}
            />
          </div>
          <div>
            <Label htmlFor="fa-location">{t('fa.fLocation')}</Label>
            <Input
              id="fa-location"
              defaultValue={asset.location ?? ''}
              disabled={frozen}
              onBlur={(event) => {
                if (event.target.value !== (asset.location ?? '')) {
                  patch({ location: event.target.value });
                }
              }}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="fa-life">{t('fa.fLife')}</Label>
              <Input
                id="fa-life"
                inputMode="numeric"
                defaultValue={asset.lifeValue !== null ? String(asset.lifeValue) : ''}
                disabled={frozen}
                onBlur={(event) => {
                  const value = Number.parseInt(event.target.value, 10);
                  if (Number.isInteger(value) && value !== asset.lifeValue) {
                    patch({ lifeValue: value });
                  }
                }}
              />
            </div>
            <div className="w-32">
              <Label htmlFor="fa-life-unit">{t('fa.fLifeUnit')}</Label>
              <NativeSelect
                id="fa-life-unit"
                value={asset.lifeUnit}
                disabled={frozen}
                onChange={(event) => patch({ lifeUnit: event.target.value as 'years' | 'months' })}
              >
                <option value="years">{t('fa.unitYears')}</option>
                <option value="months">{t('fa.unitMonths')}</option>
              </NativeSelect>
            </div>
          </div>
          <div>
            <Label htmlFor="fa-method">{t('fa.fMethod')}</Label>
            <NativeSelect
              id="fa-method"
              value={asset.method ?? ''}
              disabled={frozen}
              onChange={(event) => {
                if (event.target.value !== '') {
                  patch({ method: event.target.value as DepreciationMethod });
                }
              }}
            >
              {/* D32: absent until chosen or brought by a category (§5b). */}
              {asset.method === null ? <option value="">{t('common.choose')}</option> : null}
              {DEPRECIATION_METHODS.map((method) => (
                <option key={method} value={method}>
                  {t(METHOD_KEYS[method])}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="fa-status">{t('fa.fStatus')}</Label>
            <NativeSelect
              id="fa-status"
              value={asset.status}
              disabled={frozen}
              onChange={(event) => {
                if (event.target.value === 'draft') return;
                patch({
                  status: event.target.value as 'in_use' | 'under_maintenance' | 'fully_depreciated',
                });
              }}
            >
              {/* Disposed is NOT an option — the dialogue is the only route
                  in, because it posts an entry (the defect fixed). Draft is
                  shown only WHILE the asset is one (D32): picking a live
                  status is how a draft is activated, and the server refuses
                  — naming the missing fields — until all eight are present.
                  There is no route back to draft. */}
              {asset.status === 'draft' ? (
                <option value="draft">{t('common.status.draft')}</option>
              ) : null}
              <option value="in_use">{t('fa.stInUse')}</option>
              <option value="under_maintenance">{t('fa.stMaintenance')}</option>
              <option value="fully_depreciated">{t('fa.stFullyDepreciated')}</option>
              {asset.status === 'disposed' ? (
                <option value="disposed">{t('fa.stDisposed')}</option>
              ) : null}
            </NativeSelect>
            <p className="mt-1 text-xs text-muted-foreground">{t('fa.disposeViaDialogue')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Depreciation schedule (§5c). */}
      <Card>
        <CardContent className="space-y-2">
          <h3 className="font-semibold">{t('fa.scheduleTitle')}</h3>
          {asset.schedule.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fa.noSchedule')}</p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th className="p-2 text-start">{t('fa.colYear')}</th>
                    <th className="p-2 text-end">{t('fa.colCharge')}</th>
                    <th className="p-2 text-end">{t('fa.colAccumulated')}</th>
                    <th className="p-2 text-end">{t('fa.colNbv')}</th>
                  </tr>
                </thead>
                <tbody>
                  {asset.schedule.map((row) => (
                    <tr key={row.year} className="border-b last:border-0">
                      <TableCell className="px-2">
                        <Ltr value={String(row.year)} />
                        {row.proratedPct !== null ? (
                          <span className="ms-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                            {t('fa.proratedBadge', { pct: row.proratedPct })}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={row.charge} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={row.accumulated} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={row.netBookValue} />
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground">{t('fa.prorationNote')}</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Maintenance log (§5e) — posts nothing, changes no figure. */}
      <Card>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t('fa.maintenanceTitle')}</h3>
            <span className="text-sm text-muted-foreground">
              {t('fa.maintenanceTotal')} <Amount value={asset.maintenanceTotal} />
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{t('fa.maintenanceHint')}</p>
          {asset.maintenance.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fa.noMaintenance')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-2 text-start">{t('fa.colServiceDate')}</th>
                  <th className="p-2 text-end">{t('fa.colServiceCost')}</th>
                  <th className="p-2 text-start">{t('fa.colDescription')}</th>
                  <th className="p-2 text-start">{t('fa.colNextService')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {asset.maintenance.map((entry) => (
                  <tr
                    key={entry.id}
                    className={cn('border-b last:border-0', entry.removedAt !== null && 'opacity-50')}
                  >
                    <TableCell className="px-2">
                      <Ltr value={entry.serviceDate} />
                    </TableCell>
                    <TableCell className="px-2 text-end">
                      <Amount value={entry.cost} />
                    </TableCell>
                    <TableCell className="px-2">{entry.description ?? '—'}</TableCell>
                    <TableCell className="px-2">
                      {entry.nextService === null ? '—' : <Ltr value={entry.nextService} />}
                    </TableCell>
                    <TableCell className="px-2 text-end">
                      {entry.removedAt !== null ? (
                        // BR-FIN-07-19: the removal is recorded, and the row
                        // stays — the log is an audit record, not a list.
                        <span className="text-xs text-muted-foreground">{t('fa.removed')}</span>
                      ) : canEdit && asset.status !== 'disposed' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            act(
                              removeMaintenance.mutateAsync({
                                assetId: asset.id,
                                entryId: entry.id,
                              }),
                            )
                          }
                        >
                          {t('common.remove')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {asset.status !== 'disposed' ? (
            <GatedGroup permission="accounting.edit" reasonKey="perm.cannot.editAssets">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label htmlFor="fa-service-date">{t('fa.colServiceDate')}</Label>
                <Input
                  id="fa-service-date"
                  type="date"
                  className="h-9 w-40"
                  value={serviceDate}
                  onChange={(event) => setServiceDate(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="fa-service-cost">{t('fa.colServiceCost')}</Label>
                <Input
                  id="fa-service-cost"
                  inputMode="decimal"
                  className="h-9 w-28"
                  value={serviceCost}
                  onChange={(event) => setServiceCost(event.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="fa-service-note">{t('fa.colDescription')}</Label>
                <Input
                  id="fa-service-note"
                  className="h-9"
                  value={serviceNote}
                  onChange={(event) => setServiceNote(event.target.value)}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                // §5e: only the date is required.
                disabled={serviceDate === ''}
                onClick={() => {
                  act(
                    addMaintenance.mutateAsync({
                      assetId: asset.id,
                      body: {
                        serviceDate,
                        ...(serviceCost.trim() !== '' ? { cost: serviceCost.trim() } : {}),
                        ...(serviceNote.trim() !== '' ? { description: serviceNote.trim() } : {}),
                      },
                    }),
                  );
                  setServiceDate(today);
                  setServiceCost('');
                  setServiceNote('');
                }}
              >
                {t('fa.addEntry')}
              </Button>
            </div>
            </GatedGroup>
          ) : null}
        </CardContent>
      </Card>

      {disposeOpen ? (
        <DisposeModal asset={asset} onClose={() => setDisposeOpen(false)} onError={onError} />
      ) : null}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}

function DisposeModal({
  asset,
  onClose,
  onError,
}: {
  asset: AssetResponseFull;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [date, setDate] = useDocumentDate();
  const [method, setMethod] = useState<'sold' | 'scrapped' | 'donated'>('sold');
  const [proceeds, setProceeds] = useState('');
  const dispose = useDisposeAsset();

  // §6: a LIVE journal-entry preview that updates as the proceeds are typed.
  const { data: preview } = useDisposalPreview(
    asset.id,
    {
      date,
      method,
      ...(method === 'sold' && proceeds.trim() !== '' ? { proceeds: proceeds.trim() } : {}),
    },
    date !== '',
  );
  const gain = preview === undefined ? 0 : Number(preview.gainLoss);

  return (
    <Modal title={t('fa.disposeTitle')} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('fa.disposeHint')}</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="fa-dispose-date">{t('fa.fDisposalDate')}</Label>
            <Input
              id="fa-dispose-date"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            {/* BR-FIN-07-10, said where it matters. */}
            <p className="mt-1 text-xs text-muted-foreground">{t('fa.measuredAtDate')}</p>
          </div>
          <div>
            <Label htmlFor="fa-dispose-method">{t('fa.fDisposalMethod')}</Label>
            <NativeSelect
              id="fa-dispose-method"
              value={method}
              onChange={(event) => {
                setMethod(event.target.value as 'sold' | 'scrapped' | 'donated');
                setProceeds('');
              }}
            >
              <option value="sold">{t('fa.methodsold')}</option>
              <option value="scrapped">{t('fa.methodscrapped')}</option>
              <option value="donated">{t('fa.methoddonated')}</option>
            </NativeSelect>
          </div>
          {/* §6: shown only when the method is Sold. */}
          {method === 'sold' ? (
            <div>
              <Label htmlFor="fa-dispose-proceeds">{t('fa.fProceeds')}</Label>
              <Input
                id="fa-dispose-proceeds"
                inputMode="decimal"
                value={proceeds}
                onChange={(event) => setProceeds(event.target.value)}
              />
            </div>
          ) : (
            <div className="self-end text-xs text-muted-foreground">{t('fa.noProceeds')}</div>
          )}
        </div>

        {preview ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Tile label={t('fa.nbvAtDate')} value={<Amount value={preview.nbvAtDisposal} />} />
              <div
                className={cn(
                  'rounded-md border p-3',
                  gain >= 0
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950'
                    : 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950',
                )}
              >
                <p className="text-xs text-muted-foreground">
                  {gain >= 0 ? t('fa.gain') : t('fa.loss')}
                </p>
                <p
                  className={cn(
                    'text-lg font-semibold',
                    gain >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300',
                  )}
                >
                  <Amount value={preview.gainLoss} />
                </p>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-2 text-start">{t('fa.colAccount')}</th>
                  <th className="p-2 text-end">{t('fa.colDebit')}</th>
                  <th className="p-2 text-end">{t('fa.colCredit')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.entryLines.map((line) => (
                  <tr key={`${line.accountCode}-${line.debit ?? line.credit ?? ''}`} className="border-b last:border-0">
                    <TableCell className="px-2">
                      <span className="font-mono text-xs">
                        <Ltr value={line.accountCode} />
                      </span>{' '}
                      {line.accountName}
                    </TableCell>
                    <TableCell className="px-2 text-end">
                      {line.debit === null ? '' : <Amount value={line.debit} />}
                    </TableCell>
                    <TableCell className="px-2 text-end">
                      {line.credit === null ? '' : <Amount value={line.credit} />}
                    </TableCell>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={dispose.isPending || date === ''}
            onClick={() => {
              onError('');
              dispose
                .mutateAsync({
                  assetId: asset.id,
                  body: {
                    disposalDate: date,
                    method,
                    ...(method === 'sold' && proceeds.trim() !== ''
                      ? { proceeds: proceeds.trim() }
                      : {}),
                  },
                })
                .then(onClose)
                .catch((cause: unknown) => {
                  onError(localizeServerCause(t, cause, 'fa.errSaveFailed'));
                });
            }}
          >
            {t('fa.disposeAndPost')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RunDepreciationModal({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(thisMonth());
  const { data: preview } = useDepreciationPreview(month, /^\d{4}-\d{2}$/.test(month));
  const post = usePostDepreciation();

  return (
    <Modal title={t('fa.runTitle')} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('fa.runHint')}</p>
        <div className="w-48">
          <Label htmlFor="fa-run-month">{t('fa.runMonth')}</Label>
          <Input
            id="fa-run-month"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>

        {preview ? (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-2 text-start">{t('fa.colAsset')}</th>
                  <th className="p-2 text-end">{t('fa.colCharge')}</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.length === 0 ? (
                  <tr>
                    <TableCell colSpan={2} className="px-4 text-center text-muted-foreground">
                      {localizeServerMessage(t, FA_MESSAGES.nothingEligible)}
                    </TableCell>
                  </tr>
                ) : (
                  preview.lines.map((line) => (
                    <tr key={line.assetId} className="border-b last:border-0">
                      <TableCell className="px-2">
                        {line.name}{' '}
                        <span className="font-mono text-xs text-muted-foreground">
                          <Ltr value={line.code} />
                        </span>
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={line.amount} />
                      </TableCell>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* The consolidated entry preview: the total under BOTH columns. */}
            <div className="rounded-md border p-3 text-sm">
              <p className="mb-2 font-medium">{t('fa.entryPreview')}</p>
              <div className="flex justify-between">
                <span>{t('fa.depreciationExpense')}</span>
                <Amount value={preview.total} />
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span className="ps-4">{t('fa.accumulatedDepreciation')}</span>
                <Amount value={preview.total} />
              </div>
            </div>

            {preview.excluded.length > 0 ? (
              <ul className="space-y-1 text-xs text-muted-foreground">
                {preview.excluded.map((row) => (
                  <li key={row.assetId}>
                    <Ltr value={row.code} /> {row.name} — {row.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            // The run posts only if at least one asset is eligible.
            disabled={post.isPending || (preview?.lines.length ?? 0) === 0}
            onClick={() => {
              onError('');
              post
                .mutateAsync(month)
                .then(onClose)
                .catch((cause: unknown) => {
                  onError(localizeServerCause(t, cause, 'fa.errSaveFailed'));
                });
            }}
          >
            {t('fa.postRun')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CategoryDefaultsModal({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { data: categories } = useAssetCategories();
  const update = useUpdateCategory();

  const act = (promise: Promise<unknown>) => {
    onError('');
    promise.catch((cause: unknown) => {
      onError(localizeServerCause(t, cause, 'fa.errSaveFailed'));
    });
  };

  return (
    <Modal title={t('fa.categoryDefaults')} onClose={onClose}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="p-2 text-start">{t('fa.colCategory')}</th>
            <th className="p-2 text-start">{t('fa.colDefaultLife')}</th>
            <th className="p-2 text-start">{t('fa.colDefaultMethod')}</th>
            <th className="p-2 text-start">{t('fa.colCostAccount')}</th>
          </tr>
        </thead>
        <tbody>
          {(categories ?? []).map((category) => (
            <tr key={category.id} className="border-b last:border-0">
              <TableCell className="px-2">
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="size-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  {t(CATEGORY_KEYS[category.key])}
                  <span className="text-xs text-muted-foreground">
                    ({category.assetCount})
                  </span>
                </span>
              </TableCell>
              <TableCell className="px-2">
                <Input
                  aria-label={t('fa.lifeFor', { category: t(CATEGORY_KEYS[category.key]) })}
                  inputMode="numeric"
                  className="h-9 w-20"
                  defaultValue={String(category.defaultLifeYears)}
                  onBlur={(event) => {
                    const value = Number.parseInt(event.target.value, 10);
                    if (Number.isInteger(value) && value !== category.defaultLifeYears) {
                      act(
                        update.mutateAsync({
                          categoryId: category.id,
                          body: { defaultLifeYears: value },
                        }),
                      );
                    }
                  }}
                />
              </TableCell>
              <TableCell className="px-2">
                <NativeSelect
                  aria-label={t('fa.methodFor', { category: t(CATEGORY_KEYS[category.key]) })}
                  className="h-9 w-48"
                  value={category.defaultMethod}
                  onChange={(event) =>
                    act(
                      update.mutateAsync({
                        categoryId: category.id,
                        body: { defaultMethod: event.target.value as DepreciationMethod },
                      }),
                    )
                  }
                >
                  {DEPRECIATION_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {t(METHOD_KEYS[method])}
                    </option>
                  ))}
                </NativeSelect>
              </TableCell>
              <TableCell className="px-2 font-mono text-xs">
                <Ltr value={category.costAccountCode} />
              </TableCell>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-muted-foreground">{t('fa.categoryDefaultsHint')}</p>
    </Modal>
  );
}

function RunHistoryCard() {
  const { t } = useTranslation();
  const { data } = useDepreciationRuns();
  const runs = data ?? [];

  return (
    <Card>
      <CardContent className="space-y-2">
        <h2 className="font-semibold">{t('fa.runsTitle')}</h2>
        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('fa.noRuns')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="p-2 text-start">{t('fa.colRun')}</th>
                <th className="p-2 text-start">{t('fa.colMonth')}</th>
                <th className="p-2 text-end">{t('fa.colAssets')}</th>
                <th className="p-2 text-end">{t('fa.colCharge')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id} className="border-b last:border-0">
                  <TableCell className="px-2 font-mono">
                    <Ltr value={run.code} />
                  </TableCell>
                  <TableCell className="px-2">
                    <Ltr value={run.month} />
                  </TableCell>
                  <TableCell className="px-2 text-end">{run.lines.length}</TableCell>
                  <TableCell className="px-2 text-end">
                    <Amount value={run.total} />
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
